import uiService from './UIService';
import utilService from './UtilService';

const scrollService = {
    easeIn(fraction) {
        // Easing function: easeInOutCubic
        // From: https://gist.github.com/gre/1650294
        return fraction < 0.5 ?
            4 * fraction * fraction * fraction
            :
            ((fraction - 1) * ((2 * fraction) - 2) * ((2 * fraction) - 2)) + 1;
    },

    getElementY(element) {
        if (element && element.id.includes('scorableItem-')) {
            const split = element.id.split(/scorableItem-|_/);
            const questionContainer = element.closest(`#question-${split[1]}`);

            const offsetRelativeToQuestion = element.getBoundingClientRect().y - questionContainer.getBoundingClientRect().y;

            return questionContainer.offsetTop + offsetRelativeToQuestion;
        }
        return element.offsetTop;
    },

    getSectionContentPaddingTop(sectionContentElement) {
        const sectionContentStyling = window.getComputedStyle(sectionContentElement, null);
        let paddingTopValue;
        if (sectionContentStyling !== null) {
            paddingTopValue = sectionContentStyling.getPropertyValue('padding-top');
        }
        return typeof paddingTopValue === 'string' ?
            parseInt(paddingTopValue, 10)
            : 0;
    },

    getHeaderSize() {
        const header = document.getElementById('header');
        return header?.offsetHeight || 0;
    },

    // element.scrollTop works only when overflow-y equals scroll (auto)
    // otherwise need to scroll the whole page
    // it happens for zoomed in content or small frame (height < 636px)
    isContentScrollable() {
        const appContentContainer = document.getElementById('appContentContainer');
        const overflowY = uiService.getElementStyleProperty(appContentContainer, 'overflow-y');
        return ['auto', 'scroll'].includes(overflowY);
    },

    evaluateElementScrollApproach(element, positionY) {
        element.scrollTop = positionY;
    },

    evaluateWindowScrollApproach(positionY) {
        window.scrollTo(0, positionY);
    },

    makeScrollStep(params, timestamp) {
        const { start, diff, startingY, duration, evaluateScrolling, resolver } = params;
        const durationToUse = duration > 100 ? duration : 100;

        // Elapsed milliseconds since start of scrolling.
        const time = timestamp - start;
        // Get percent of completion in range [0, 1].
        let percent = Math.min(time / durationToUse, 1);
        percent = scrollService.easeIn(percent);
        evaluateScrolling(startingY + (diff * percent));

        // Proceed with animation as long as we wanted it to.
        if (time < durationToUse) {
            window.requestAnimationFrame(this.makeScrollStep.bind(this, params));
        } else {
            resolver();
        }
    },

    /**
     * @return {Promise}
     */
    scrollToElement(element, duration, offsetFromTop, scrollableElementId = 'sectionContent') {
        return new Promise((resolve, reject) => {
            try {
                const scrollableElement = document.getElementById(scrollableElementId);
                const isElementScrollable = this.isContentScrollable();

                let offsetFromTopClone;
                if (!offsetFromTop) {
                    offsetFromTopClone = this.getSectionContentPaddingTop(scrollableElement);
                    if (!isElementScrollable) {
                        offsetFromTopClone += this.getHeaderSize();
                    }
                } else {
                    offsetFromTopClone = offsetFromTop;
                }

                const { scrollHeight } = scrollableElement;
                const startingY = isElementScrollable ? scrollableElement.scrollTop : window.scrollY;
                // If element is close to page's bottom then window will scroll only to some position above the element.
                let targetY;
                if (isElementScrollable) {
                    const elementY = this.getElementY(element);
                    if (scrollHeight - elementY < scrollableElement.clientHeight) {
                        targetY = (scrollHeight - scrollableElement.clientHeight);
                    } else {
                        targetY = elementY - offsetFromTopClone;
                    }
                } else {
                    targetY = (element.getBoundingClientRect().y + startingY) - offsetFromTopClone;
                }

                const diff = targetY - startingY;
                if (!diff) {
                    return;
                }

                if (utilService.isHeadless()) {
                    scrollableElement.scrollTop = targetY;
                    resolve();
                    return;
                }

                const evaluateScrolling = isElementScrollable
                    ? this.evaluateElementScrollApproach.bind(this, scrollableElement)
                    : this.evaluateWindowScrollApproach.bind(this);

                window.requestAnimationFrame((timestamp) => {
                    const scrollParams = {
                        diff,
                        duration,
                        evaluateScrolling,
                        resolver: resolve.bind(this, element),
                        start: timestamp,
                        startingY,
                    };
                    this.makeScrollStep(scrollParams, timestamp);
                });
            } catch (error) {
                reject(error);
            }
        });
    },

    /**
     * @return {Promise}
     */
    scrollToFirstInteraction() {
        const res = document.getElementsByClassName('interaction');
        const element = res ? res[0] : null;

        if (!element) {
            return Promise.reject();
        }

        return this.scrollToElement(element, 500, 0);
    },

    scrollToQuestion(contentRevisionId, scrollableElementId, duration = 100) {
        const questionElement = document.getElementById(`question-${contentRevisionId}`);
        if (questionElement) {
            this.scrollToElement(questionElement, duration, 0, scrollableElementId);
        }
    },

    scrollContentToTop(elementToScroll) {
        this.scrollToElement(elementToScroll, 500, 0, 'appContentContainer');
    },

    scrollToTop() {
        const scrollableElement = document.getElementById('sectionContent');
        if (scrollableElement) {
            scrollableElement.scrollTop = 0;
        }
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 100);
        });
    },

    scrollToBottom() {
        const scrollableElement = document.getElementById('sectionContent');
        scrollableElement.scrollTop = scrollableElement.scrollHeight;
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, 100);
        });
    },
};

export default scrollService;
